/**
 * Monstees playful touches.
 *
 * <s-tickle lines="a|b|c" [autoplay]> — Wraps a button holding a doodle monster. Each tap (and, with
 *   autoplay, scrolling it into view) wiggles the
 *   monster and shows a random one-liner in a speech bubble ([data-tickle-bubble]) that
 *   auto-hides. The bubble is a polite live region so the joke is announced once.
 *
 * Cart "Yum!" — standard-actions-override dispatches `s-cart:added` after a successful add.
 *   When the cart drawer opens shortly after (`s-cart:opened`), the drawer's [data-yum]
 *   monster pops up and the [data-cart-count-badge] bounces. Manual drawer opens stay quiet.
 *
 * All motion is CSS and gated on prefers-reduced-motion there; this file only toggles classes.
 */

const BUBBLE_MS = 2600;
const YUM_MS = 2200;
const ADD_WINDOW_MS = 5000;
const AUTO_GAP_MS = 3000;
const AUTO_MAX = 4;

class STickle extends HTMLElement {
  #last = -1;
  #timer = 0;
  #autoTimer = 0;
  #autoCount = 0;
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    if (this.hasAttribute('autoplay') && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.6 });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    clearTimeout(this.#autoTimer);
  }

  // Autoplay: pop a line as the monster scrolls into view, then a few more while it stays
  // on screen (capped per page view so it never nags). Taps always work.
  #onIntersect = (entries) => {
    const visible = entries.some((entry) => entry.isIntersecting);
    clearTimeout(this.#autoTimer);
    if (!visible || this.#autoCount >= AUTO_MAX) return;
    this.#autoTimer = setTimeout(this.#autoSay, 400);
  };

  #autoSay = () => {
    if (this.#autoCount >= AUTO_MAX) return;
    this.#autoCount += 1;
    this.#say(false);
    if (this.#autoCount < AUTO_MAX) this.#autoTimer = setTimeout(this.#autoSay, AUTO_GAP_MS);
  };

  #onClick = (event) => {
    if (!event.target.closest('button')) return;
    this.#say(true);
  };

  #say(fromTap) {
    const lines = (this.getAttribute('lines') || '').split('|').map((l) => l.trim()).filter(Boolean);
    const bubble = this.querySelector('[data-tickle-bubble]');
    if (!lines.length || !bubble) return;

    let index = Math.floor(Math.random() * lines.length);
    if (lines.length > 1 && index === this.#last) index = (index + 1) % lines.length;
    this.#last = index;

    // Only announce lines the shopper asked for; automatic ones stay silent for screen readers.
    if (fromTap) bubble.setAttribute('role', 'status');
    else bubble.removeAttribute('role');

    bubble.textContent = lines[index];
    this.classList.remove('is-tickled');
    // Force reflow so repeated taps restart the wiggle.
    void this.offsetWidth;
    this.classList.add('is-tickled', 'is-talking');

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.classList.remove('is-talking', 'is-tickled');
    }, BUBBLE_MS);
  }
}

if (!customElements.get('s-tickle')) customElements.define('s-tickle', STickle);

let lastAddAt = 0;
let yumTimer = 0;

document.addEventListener('s-cart:added', () => {
  lastAddAt = Date.now();
});

document.addEventListener('s-cart:opened', (event) => {
  if (!lastAddAt || Date.now() - lastAddAt > ADD_WINDOW_MS) return;
  lastAddAt = 0;

  const drawer = event.target instanceof Element ? event.target : document.getElementById('cart-drawer');
  const yum = drawer?.querySelector('[data-yum]');
  const badge = drawer?.querySelector('[data-cart-count-badge]');

  if (badge) {
    badge.classList.remove('is-bouncing');
    void badge.offsetWidth;
    badge.classList.add('is-bouncing');
  }
  if (!yum) return;

  yum.hidden = false;
  yum.classList.remove('is-popping');
  void yum.offsetWidth;
  yum.classList.add('is-popping');

  clearTimeout(yumTimer);
  yumTimer = setTimeout(() => {
    yum.classList.remove('is-popping');
    yum.hidden = true;
  }, YUM_MS);
});

/**
 * <s-age-chart> — Homepage "Shop by age" growth chart. The monsters are an ARIA tablist:
 *   tapping one (or arrowing to it) selects it, shows its size panel and wiggles the monster.
 *   Without JS the first panel stays visible, so shoppers can still reach a filtered list.
 */
class SAgeChart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  #select(tab, focus = false) {
    for (const other of this.#tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    }
    tab.classList.remove('is-tickled');
    void tab.offsetWidth;
    tab.classList.add('is-tickled');
    if (focus) tab.focus();
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.#select(tab);
  };

  #onKeydown = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    const tabs = this.#tabs;
    const index = tabs.indexOf(tab);
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = (index + 1) % tabs.length;
    else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    this.#select(tabs[next], true);
  };
}

if (!customElements.get('s-age-chart')) customElements.define('s-age-chart', SAgeChart);
